"use client";

import { useState } from "react";
import { IconKey } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import { SectionDetailLayout } from "@components/page/section-detail-layout";
import { Button } from "@components/ui/button";
import { localizeFrom } from "@lib/i18n/utils";
import { useGetPersonalAccessTokenRootQuery } from "@features/personal-access-tokens/personal-access-token-context-api";
import routes from "@features/routes";
import { readAdmittedScopes } from "@features/personal-access-tokens/personal-access-token-view-model";
import { useDeletePersonalAccessTokenMutation, useGetMyPersonalAccessTokensQuery, useIssuePersonalAccessTokenMutation, useRenamePersonalAccessTokenMutation, useRevokePersonalAccessTokenMutation } from "@features/personal-access-tokens/personal-access-tokens-api";
import { PersonalAccessTokenIssueDialog } from "@features/personal-access-tokens/ui/personal-access-token-issue-dialog";
import { PersonalAccessTokenList } from "@features/personal-access-tokens/ui/personal-access-token-list";
import { PersonalAccessTokenSecretDialog } from "@features/personal-access-tokens/ui/personal-access-token-secret-dialog";
import type { IssuedPersonalAccessToken } from "@features/personal-access-tokens/types";
import { useGetWorkspacesQuery } from "@features/workspaces/workspaces-api";
const hasAlreadyRevokedCode = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("data" in error)) return false;
  const data = error.data;
  return typeof data === "object" && data !== null && "code" in data ? data.code === "TOKEN_ALREADY_REVOKED" : false;
};
export const OwnerTokensPage = () => {
  const t = useTranslations("features.personal-access-tokens.ui.ownerTokensPage");
  const locale = useLocale();
  const {
    data: tokens,
    isLoading,
    isError,
    refetch
  } = useGetMyPersonalAccessTokensQuery();
  const rootQuery = useGetPersonalAccessTokenRootQuery();
  const allowedScopes = rootQuery.data ? readAdmittedScopes(rootQuery.data) : null;
  const isRootLoading = rootQuery.isLoading || !rootQuery.data && rootQuery.isFetching;
  const shouldShowScopeError = allowedScopes === null && !isRootLoading;
  const {
    data: workspaces
  } = useGetWorkspacesQuery(undefined);
  const [issueToken, {
    isLoading: isIssuing,
    reset: resetIssueMutation
  }] = useIssuePersonalAccessTokenMutation();
  const [renameToken, {
    isLoading: isRenaming
  }] = useRenamePersonalAccessTokenMutation();
  const [revokeToken, {
    isLoading: isRevoking
  }] = useRevokePersonalAccessTokenMutation();
  const [deleteToken, {
    isLoading: isDeleting
  }] = useDeletePersonalAccessTokenMutation();
  const [isIssueOpen, setIsIssueOpen] = useState(false);
  const [issuedToken, setIssuedToken] = useState<IssuedPersonalAccessToken | null>(null);
  const workspaceOptions = workspaces?.map(workspace => ({
    id: workspace.id,
    label: localizeFrom(workspace.content?.data?.title, locale, workspace.hint ?? workspace.id)
  })) ?? [];
  const handleIssue = async (request: Parameters<typeof issueToken>[0]) => {
    const result = await issueToken(request).unwrap();
    if (!result || typeof result.secret !== "string" || result.secret.length === 0) {
      throw new Error("Personal access token issue returned no one-time secret");
    }
    setIssuedToken(result);
    setIsIssueOpen(false);
    return result;
  };
  const handleSecretClose = () => {
    resetIssueMutation();
    setIssuedToken(null);
  };
  const content = !tokens && isLoading ? <p role="status" className="py-8 text-center text-sm text-muted-foreground">
        {t("loading")}
      </p> : !tokens && isError ? <div role="alert" className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-sm text-muted-foreground">{t("errors.load")}</p>
        <Button type="button" variant="outline" onClick={() => void refetch()}>
          {t("errors.retry")}
        </Button>
      </div> : <PersonalAccessTokenList tokens={tokens ?? []} isPending={isRenaming || isRevoking || isDeleting || isIssuing} onIssue={allowedScopes && allowedScopes.length > 0 ? () => setIsIssueOpen(true) : undefined} onRename={async (id, name) => {
    await renameToken({
      id,
      name
    }).unwrap();
  }} onRevoke={async (id_0, reason) => {
    try {
      await revokeToken({
        id: id_0,
        ...(reason === undefined ? {} : {
          reason
        })
      }).unwrap();
    } catch (error) {
      if (!hasAlreadyRevokedCode(error)) throw error;
      await refetch().unwrap();
      return "already-revoked";
    }
  }} onDelete={async id_1 => {
    await deleteToken({
      id: id_1
    }).unwrap();
  }} />;
  return <SectionDetailLayout activeId="tokens" items={[{
    id: "tokens",
    label: t("navigation"),
    icon: <IconKey aria-hidden="true" stroke={1.5} />,
    href: routes.personal_access_tokens.pages.owner.path()
  }]}>
      <section className="space-y-6" aria-labelledby="personal-access-tokens-title">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <h2 id="personal-access-tokens-title" className="text-2xl font-semibold tracking-tight">
              {t("title")}
            </h2>
            <p className="text-sm text-muted-foreground">{t("description")}</p>
          </div>
          {allowedScopes && allowedScopes.length > 0 && tokens?.some(token => !token.deletedAt) && <Button type="button" onClick={() => setIsIssueOpen(true)}>
                {t("issue.openButton")}
              </Button>}
        </header>
        {isRootLoading && <p role="status" className="text-sm text-muted-foreground">
            {t("issue.loadingScopes")}
          </p>}
        {shouldShowScopeError && <div role="alert" className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-destructive">{t("issue.errors.scopesUnavailable")}</p>
            <Button type="button" variant="outline" disabled={rootQuery.isFetching} onClick={() => void rootQuery.refetch()}>
              {t("issue.retryScopes")}
            </Button>
          </div>}
        {allowedScopes?.length === 0 && <p role="status" className="text-sm text-muted-foreground">
            {t("issue.noScopes")}
          </p>}
        {content}
        {allowedScopes !== null && <PersonalAccessTokenIssueDialog isOpen={isIssueOpen} mode="owner" allowedScopes={allowedScopes} workspaceOptions={workspaceOptions} isRetryingAdmittedScopes={rootQuery.isFetching} onRetryAdmittedScopes={() => void rootQuery.refetch()} onIssue={handleIssue} onClose={() => setIsIssueOpen(false)} />}
        {issuedToken && <PersonalAccessTokenSecretDialog isOpen issued={issuedToken} onClose={handleSecretClose} />}
      </section>
    </SectionDetailLayout>;
};